import { useMemo } from "react"
import { Typography, Box, Stack } from "@mui/material"

import { type FC, Container, Link } from ".."
import { kebabCase } from "lodash"
type SitemapListProps = {
  data: string[]
}

// Helper function to convert kebab-case to normal case
const unKebabCase = (str: string) => {
  return str
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ")
}

const SitemapList: FC<SitemapListProps> = ({ data }) => {
  const linkMap = useMemo(() => {
    // Create a map of the links
    const map = new Map<string, { title: string; uri: string }[]>()
    // Loop through the data and create a map of the links
    data.forEach((item) => {
      const linkArray = item.split("/")
      const key = linkArray[1]
      const value = {
        title: linkArray
          .slice(2)
          .map((s) => unKebabCase(s))
          .join(" > "),
        uri: item,
      }
      if (linkArray.length > 2) {
        map.set(key, [...(map.get(key) || []), value])
      } else {
        map.set("landing-pages", [
          ...(map.get("landing-pages") || []),
          {
            title: unKebabCase(key),
            uri: item,
          },
        ])
      }
    })
    // Reformat the map to have the key in normal case and the values sorted
    const formattedMap = new Map<string, { title: string; uri: string }[]>()
    map.forEach((value, key) => {
      formattedMap.set(
        unKebabCase(key),
        value.sort((a, b) => a.title.localeCompare(b.title))
      )
    })

    // Remove the Node key - these links are not needed
    formattedMap.delete("Node")

    return formattedMap
  }, [data])

  const keys = Array.from(linkMap.keys()).sort()

  return (
    <Container>
      <Box py={25}>
        <Typography variant="heading.2xl" component="h1" mb={10}>
          Goway Sitemap
        </Typography>
        {keys.map((key) => (
          <Box
            key={key}
            sx={{
              pb: 10,
              mb: 10,
              borderBottom: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
            }}
          >
            {key && (
              <Typography variant="heading.sm" component="h2" mb={6}>
                <Link
                  variant="heading.sm"
                  href={`/${kebabCase(key)}`}
                  type="underline-reverse"
                  aria-label={`Go to Page: ${key}`}
                >
                  {key}
                </Link>
              </Typography>
            )}
            <Stack spacing={5} pl={3} alignItems="flex-start">
              {linkMap.get(key)?.map(({ title, uri }) => (
                <Link
                  href={`/${uri}`}
                  key={uri}
                  type="underline-reverse"
                  aria-label={`Go to Page: ${title}`}
                >
                  {title}
                </Link>
              ))}
            </Stack>
          </Box>
        ))}
      </Box>
    </Container>
  )
}
export default SitemapList
